import React from 'react'
import PropTypes from 'prop-types'
import styled, { useTheme } from 'styled-components'
import Spinner from 'Components/Spinner'
import { Button as ShadcnButton } from '../ui/button'

// Spinner wrapper for better positioning during loading
const SpinnerWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
`

// Styled wrapper to integrate theme-based styling with shadcn Button
const ThemedButton = styled(ShadcnButton)`
  /* Override shadcn styles with theme-based styles */
  --primary: ${props => props.theme.color.brand};
  --primary-foreground: ${props => props.theme.color.bg};
  --border: ${props => props.theme.color.brand};
  --background: ${props => props.theme.color.bg};
  --font-family: ${props => props.theme.fontFamily};
  
  /* Responsive base styles */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  box-sizing: border-box;
  user-select: none;
  text-align: center;
  font-weight: 600;
  line-height: 1.2;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 0.375rem; /* 6px - more modern than 2px */
  cursor: pointer;
  
  /* Responsive font sizing */
  font-size: clamp(0.875rem, 2.5vw, 1rem);
  
  /* Touch-friendly minimum size for mobile */
  min-height: 44px; /* Apple/Google recommended minimum touch target */
  min-width: 44px;
  
  /* Responsive sizing variants */
  ${props =>
    props.$customSize === 'sm' &&
    `
    padding: 0.5rem 1rem;
    gap: 0.375rem;
    
    @media (min-width: 480px) {
      padding: 0.625rem 1.25rem;
    }
    
    @media (min-width: ${props.theme.breakpoint?.md || 768}px) {
      padding: 0.625rem 1.5rem;
    }
  `}
  
  ${props =>
    props.$customSize === 'md' &&
    `
    padding: 0.625rem 1.5rem;
    gap: 0.5rem;
    
    @media (min-width: 480px) {
      padding: 0.75rem 2rem;
    }
    
    @media (min-width: ${props.theme.breakpoint?.md || 768}px) {
      padding: 0.75rem 2.5rem;
    }
    
    @media (min-width: ${props.theme.breakpoint?.lg || 1024}px) {
      padding: 0.875rem 3rem;
    }
  `}
  
  ${props =>
    props.$customSize === 'lg' &&
    `
    padding: 0.75rem 2rem;
    gap: 0.5rem;
    font-size: clamp(1rem, 3vw, 1.125rem);
    
    @media (min-width: 480px) {
      padding: 0.875rem 2.5rem;
    }
    
    @media (min-width: ${props.theme.breakpoint?.md || 768}px) {
      padding: 1rem 3rem;
    }
    
    @media (min-width: ${props.theme.breakpoint?.lg || 1024}px) {
      padding: 1rem 4rem;
    }
    
    @media (min-width: ${props.theme.breakpoint?.xl || 1280}px) {
      padding: 1.125rem 5rem;
    }
  `}
  
  ${props =>
    props.$customSize === 'sleek' &&
    `
    padding: 0.625rem 1.25rem;
    gap: 0.375rem;
    font-size: 0.875rem;
    
    @media (min-width: 480px) {
      padding: 0.75rem 1.5rem;
      font-size: 0.9375rem;
    }
    
    @media (min-width: ${props.theme.breakpoint?.md || 768}px) {
      padding: 0.75rem 2rem;
      font-size: 1rem;
    }
    
    @media (min-width: ${props.theme.breakpoint?.lg || 1024}px) {
      padding: 0.875rem 2.5rem;
    }
  `}
  
  ${props =>
    props.$customSize === 'slim' &&
    `
    padding: 0.5rem 0.75rem;
    gap: 0.25rem;
    font-size: 0.875rem;
    
    @media (min-width: ${props.theme.breakpoint?.md || 768}px) {
      padding: 0.625rem 1rem;
      font-size: 0.9375rem;
    }
  `}
  
  /* Responsive width behavior */
  width: ${props => (props.$fullWidth ? '100%' : 'auto')};
  max-width: ${props => (props.$fullWidth ? '100%' : 'min(100%, 400px)')};
  
  @media (max-width: 480px) {
    ${props =>
      props.$responsiveFullWidth &&
      `
      width: 100%;
    `}
  }
  
  /* Text overflow handling for narrow screens */
  overflow: hidden;
  white-space: ${props => (props.$allowWrap ? 'normal' : 'nowrap')};
  text-overflow: ellipsis;
  
  @media (max-width: 320px) {
    white-space: normal;
    word-break: break-word;
    hyphens: auto;
  }
  
  /* Custom type styling with better contrast and accessibility */
  ${props =>
    props.$customType === 'default' &&
    `
    background-color: ${props.theme.color.brand} !important;
    color: #ffffff !important;
    border: 2px solid ${props.theme.color.brand};
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
    
    &:hover:not(:disabled) {
      background-color: ${props.theme.color.brand} !important;
      color: #ffffff !important;
      opacity: 0.9;
      transform: translateY(-1px);
    }
    
    &:focus:not(:disabled) {
      background-color: ${props.theme.color.brand} !important;
      color: #ffffff !important;
    }
    
    &:active:not(:disabled) {
      transform: translateY(0);
    }
  `}
  
  ${props =>
    props.$customType === 'alternative' &&
    `
    border: 2px solid ${props.theme.color.brand};
    color: ${props.theme.color.brand} !important;
    background-color: ${props.theme.color.bg} !important;
    
    &:hover:not(:disabled) {
      background-color: ${props.theme.color.brand} !important;
      color: #ffffff !important;
      transform: translateY(-1px);
    }
    
    &:focus:not(:disabled) {
      background-color: ${props.theme.color.brand} !important;
      color: #ffffff !important;
    }
    
    &:active:not(:disabled) {
      transform: translateY(0);
    }
  `}
  
  ${props =>
    props.$customType === 'text' &&
    `
    border: 2px solid transparent;
    color: ${props.theme.color.brand} !important;
    background-color: transparent !important;
    
    &:hover:not(:disabled) {
      background-color: ${props.theme.color.brand} !important;
      color: #ffffff !important;
      transform: translateY(-1px);
    }
    
    &:focus:not(:disabled) {
      background-color: ${props.theme.color.brand} !important;
      color: #ffffff !important;
    }
    
    &:active:not(:disabled) {
      transform: translateY(0);
    }
  `}
  
  /* Enhanced disabled state */
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
    box-shadow: none !important;
    pointer-events: none;
  }
  
  /* Loading state improvements */
  &[data-loading="true"] {
    pointer-events: none;
    position: relative;
    
    /* Hide text during loading but maintain button size */
    color: transparent !important;
    
    /* Hide content during loading */
    & > *:not(${SpinnerWrapper}) {
      opacity: 0;
    }
  }
  
  /* Focus states for better accessibility */
  &:focus-visible {
    outline: 2px solid ${props => props.theme.color.brand};
    outline-offset: 2px;
  }
  
  /* Ensure text color is maintained on focus-visible */
  &:focus-visible:not(:disabled) {
    ${props =>
      props.$customType === 'default' &&
      `
      color: #ffffff !important;
    `}
    ${props =>
      props.$customType === 'alternative' &&
      `
      background-color: ${props.theme.color.brand} !important;
      color: #ffffff !important;
    `}
    ${props =>
      props.$customType === 'text' &&
      `
      background-color: ${props.theme.color.brand} !important;
      color: #ffffff !important;
    `}
  }
  
  /* High contrast mode support */
  @media (prefers-contrast: high) {
    border-width: 3px;
  }
  
  /* Reduced motion support */
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    transform: none !important;
    
    &:hover:not(:disabled),
    &:active:not(:disabled) {
      transform: none !important;
    }
  }
  
  /* Custom style overrides */
  ${props =>
    props.$customStyle &&
    Object.entries(props.$customStyle)
      .map(([key, value]) => {
        const cssKey = key.replace(/[A-Z]/g, m => '-' + m.toLowerCase())
        const cssValue = typeof value === 'number' ? `${value}px` : value
        return `${cssKey}: ${cssValue};`
      })
      .join(' ')}
`

const Button = ({
  disabled,
  loading,
  children,
  size = 'sm',
  type = 'normal',
  onClick,
  style = {},
  className,
  fullWidth = false,
  responsiveFullWidth = false,
  allowWrap = false,
  ...rest
}) => {
  const theme = useTheme()

  // Map legacy type prop to shadcn variant
  const getShadcnVariant = () => {
    switch (type) {
      case 'alternative':
        return 'outline'
      case 'text':
        return 'ghost'
      case 'default':
      case 'normal':
      default:
        return 'default'
    }
  }

  // Map legacy size prop to shadcn size
  const getShadcnSize = () => {
    switch (size) {
      case 'lg':
        return 'lg'
      case 'sm':
      case 'md':
      case 'sleek':
      case 'slim':
      default:
        return 'default'
    }
  }

  return (
    <ThemedButton
      type="button"
      disabled={disabled || loading}
      variant={getShadcnVariant()}
      size={getShadcnSize()}
      onClick={onClick}
      className={className}
      theme={theme}
      data-loading={loading}
      $customSize={size}
      $customType={type === 'normal' ? 'default' : type}
      $customStyle={style}
      $fullWidth={fullWidth}
      $responsiveFullWidth={responsiveFullWidth}
      $allowWrap={allowWrap}
      {...rest}
    >
      {loading && (
        <SpinnerWrapper>
          <Spinner />
        </SpinnerWrapper>
      )}
      {children}
    </ThemedButton>
  )
}

Button.propTypes = {
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'sleek', 'slim']),
  onClick: PropTypes.func,
  type: PropTypes.oneOf(['default', 'alternative', 'text', 'normal']),
  children: PropTypes.node,
  className: PropTypes.string,
  style: PropTypes.object,
  fullWidth: PropTypes.bool,
  responsiveFullWidth: PropTypes.bool,
  allowWrap: PropTypes.bool
}

export default Button
